import { z } from 'zod'
import { translate, type Translator } from './i18n.ts'

export const USERNAME_MIN_LENGTH = 3
export const USERNAME_MAX_LENGTH = 20

// Schema factories: messages resolve via the caller's translator — actions
// pass `getTranslator(request)`, client-side `onValidate` passes
// `useTranslation()`.

export const createUsernameSchema = (t: Translator) =>
	z
		.string({ required_error: t('error.username.required') })
		.min(USERNAME_MIN_LENGTH, { message: t('error.username.tooShort') })
		.max(USERNAME_MAX_LENGTH, { message: t('error.username.tooLong') })
		.regex(/^[a-zA-Z0-9_]+$/, {
			message: t('error.username.invalid'),
		})
		// users can type the username in any case, but we store it in lowercase
		.transform((value) => value.toLowerCase())

export const createPasswordSchema = (t: Translator) =>
	z
		.string({ required_error: t('error.password.required') })
		.min(6, { message: t('error.password.tooShort') })
		// NOTE: bcrypt has a limit of 72 bytes (which should be plenty long)
		// https://github.com/epicweb-dev/epic-stack/issues/918
		.refine((val) => new TextEncoder().encode(val).length <= 72, {
			message: t('error.password.tooLong'),
		})

export const createNameSchema = (t: Translator) =>
	z
		.string({ required_error: t('error.name.required') })
		.min(3, { message: t('error.name.tooShort') })
		.max(40, { message: t('error.name.tooLong') })

export const createEmailSchema = (t: Translator) =>
	z
		.string({ required_error: t('error.email.required') })
		.email({ message: t('error.email.invalid') })
		.min(3, { message: t('error.email.tooShort') })
		.max(100, { message: t('error.email.tooLong') })
		// users can type the email in any case, but we store it in lowercase
		.transform((value) => value.toLowerCase())

export const createPasswordAndConfirmPasswordSchema = (t: Translator) =>
	z
		.object({
			password: createPasswordSchema(t),
			confirmPassword: createPasswordSchema(t),
		})
		.superRefine(({ confirmPassword, password }, ctx) => {
			if (confirmPassword !== password) {
				ctx.addIssue({
					path: ['confirmPassword'],
					code: 'custom',
					message: t('error.password.mustMatch'),
				})
			}
		})

// EN-bound constants for the routable-but-unlinked stack surfaces
// (settings/profile/**, email-auth flows — decisions-log #12): their EN texts
// stay byte-identical (en dictionary values = the former literals). Reachable
// surfaces use the factories above with a request-locale translator.
const tEn: Translator = (key, values) => translate('en', key, values)

export const UsernameSchema = createUsernameSchema(tEn)
export const PasswordSchema = createPasswordSchema(tEn)
export const NameSchema = createNameSchema(tEn)
export const EmailSchema = createEmailSchema(tEn)
export const PasswordAndConfirmPasswordSchema =
	createPasswordAndConfirmPasswordSchema(tEn)
